import { Avatar, Button, Card, Input, Typography } from "antd";
import { useState, useEffect, useRef, useContext, useMemo } from "react";
import { DownOutlined, UpOutlined, CloseOutlined, ArrowUpOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../context/AuthContext";
import { markMessagesAsRead } from "../../utils/markMessagesAsRead";
import { useGlobalToast } from "../../context/ToastProvider";
import { isDriverMessage, isFleetMessage } from "../../utils/messageRole";
import avatarPlaceholder from "../../img/avatar_placeholder.png";
import "./FloatingChat.css";

const FLASH_DURATION_MS = 900;
const DEFAULT_CHAT_HEIGHT = 420;
const DEFAULT_CHAT_MINIMIZED_HEIGHT = 52;

const FloatingChat = ({
  id,
  name,
  messages = [],
  onSendMessage,
  isMinimized,
  profilePic,
  chat,
  status,
  statusLabel,
  onClose,
  onToggleMinimize,
  style,
  onLoadMore,
  hasMore = false,
}) => {
  const [inputMessage, setInputMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isFlashing, setIsFlashing] = useState(false);
  const primaryColor = "var(--chakra-colors-gandolapp-500)";
  const driverColor = "#f3f4f6";
  const fleetColor = "var(--chakra-colors-gandolapp-100)";
  const { t, i18n } = useTranslation();
  const { getFleetID } = useContext(AuthContext);
  const toast = useGlobalToast();

  const scrollContainerRef = useRef(null);
  const inputRef = useRef(null);
  const initialLoadDoneRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const previousScrollHeightRef = useRef(0);
  const lastMessageSignatureRef = useRef("");
  const hydratedMessagesRef = useRef(false);
  const previousMessageCountRef = useRef(0);

  const normalizeMessageText = (value) => {
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    if (value === null || value === undefined) return "";
    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  };

  const handleSend = async () => {
    if (!inputMessage.trim() || isSending) return;
    setIsSending(true);
    try {
      await onSendMessage(inputMessage);
      setInputMessage("");
    } catch (error) {
      toast({ error, title: t("toast.errorBroadcastMessage"), status: "warning" });
    } finally {
      setIsSending(false);
    }
  };

  const focusComposer = () => {
    inputRef.current?.focus?.({
      cursor: "end",
    });
    if (typeof window !== "undefined") {
      window.focus?.();
    }
  };

  const scrollToBottom = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    if (messages.length && !initialLoadDoneRef.current) {
      scrollToBottom();
      initialLoadDoneRef.current = true;
      previousMessageCountRef.current = messages.length;
      return;
    }

    if (loadingMoreRef.current) {
      const heightDelta = container.scrollHeight - previousScrollHeightRef.current;
      container.scrollTop = Math.max(0, heightDelta);
      loadingMoreRef.current = false;
      previousMessageCountRef.current = messages.length;
      return;
    }

    const hadNewMessage = messages.length > previousMessageCountRef.current;
    if (!isMinimized && hadNewMessage) {
      scrollToBottom();
    }
    previousMessageCountRef.current = messages.length;
  }, [messages, isMinimized]);

  useEffect(() => {
    if (isMinimized) return;
    const timer = setTimeout(() => {
      scrollToBottom();
      focusComposer();
    }, 0);
    return () => clearTimeout(timer);
  }, [isMinimized, chat?.key]);

  // Marcar mensajes como leídos
  useEffect(() => {
    const run = async () => {
      const fleetId = getFleetID?.();
      if (!fleetId) return;

      const transportOrderId = chat?.currentOrder || null;
      const userId = chat?.id || null;

      try {
        if (transportOrderId) {
          await markMessagesAsRead(fleetId, transportOrderId);
        } else {
          await markMessagesAsRead(fleetId, null, userId);
        }
      } catch (error) {
        console.error("Error marking chat messages as read:", error);
      }
    };
    run();
  }, [getFleetID, chat?.currentOrder, chat?.id]);

  const requestLoadMore = () => {
    if (!hasMore || !onLoadMore || loadingMoreRef.current) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    loadingMoreRef.current = true;
    previousScrollHeightRef.current = container.scrollHeight;
    onLoadMore();
  };

  const handleScroll = (e) => {
    if (e.target.scrollTop <= 2) {
      requestLoadMore();
    }
  };

  const formatMessageDate = (msg) => {
    const raw = msg.createdAt || Date.now();
    if (raw instanceof Date) return raw;
    if (typeof raw?.toDate === "function") return raw.toDate();
    if (typeof raw?.toMillis === "function") return new Date(raw.toMillis());
    if (typeof raw?._seconds === "number") return new Date(raw._seconds * 1000);
    if (typeof raw?.seconds === "number") return new Date(raw.seconds * 1000);
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return new Date();
    return date;
  };

  const getMessageSignature = (msg) => {
    if (!msg) return "";
    const dateObj = formatMessageDate(msg);
    return [
      msg.docId || "",
      msg.tempMessageId || "",
      String(msg.id || ""),
      normalizeMessageText(msg.message),
      dateObj.getTime(),
    ].join("|");
  };

  useEffect(() => {
    if (!messages.length) return;
    const lastMessage = messages[messages.length - 1];
    const nextSignature = getMessageSignature(lastMessage);

    if (!hydratedMessagesRef.current) {
      hydratedMessagesRef.current = true;
      lastMessageSignatureRef.current = nextSignature;
      return;
    }

    if (nextSignature === lastMessageSignatureRef.current) return;
    lastMessageSignatureRef.current = nextSignature;

    if (!isDriverMessage(lastMessage) || isMinimized) return;

    setIsFlashing(true);
    focusComposer();
  }, [messages, isMinimized]);

  useEffect(() => {
    if (!isFlashing) return undefined;
    const timeout = setTimeout(() => setIsFlashing(false), FLASH_DURATION_MS);
    return () => clearTimeout(timeout);
  }, [isFlashing]);

  const groupedMessages = useMemo(
    () =>
      messages.reduce((acc, msg) => {
        const dateObj = formatMessageDate(msg);
        const dateKey = dateObj.toISOString().split("T")[0];
        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push({
          ...msg,
          _dateObj: dateObj,
          _displayMessage: normalizeMessageText(msg?.message),
        });
        return acc;
      }, {}),
    [messages]
  );
  const localeTag = String(i18n?.resolvedLanguage || i18n?.language || "en").replace("_", "-");

  const headerNode = (
    <div className="floatingChat__headerContent">
      <div className="floatingChat__titleGroup">
        <div className="floatingChat__avatarWrap">
          <Avatar size={30} src={profilePic || avatarPlaceholder} />
          <span
            className="floatingChat__statusDot"
            style={{ background: status === "online" ? "#16a34a" : "#9ca3af" }}
          />
        </div>
        <div className="floatingChat__titleTexts">
          <Typography.Text strong className="floatingChat__driverName">
            {name}
          </Typography.Text>
          <Typography.Text className="floatingChat__driverStatus">
            {statusLabel || (status === "online" ? "Online" : "Offline")}
          </Typography.Text>
        </div>
      </div>
    </div>
  );

  const headerActions = (
    <div className="floatingChat__headerActions">
      <Button
        type="text"
        size="small"
        className="floatingChat__headerButton"
        icon={isMinimized ? <UpOutlined /> : <DownOutlined />}
        onClick={onToggleMinimize}
      />
      <Button
        type="text"
        size="small"
        className="floatingChat__headerButton"
        icon={<CloseOutlined />}
        onClick={onClose}
      />
    </div>
  );

  return (
    <Card
      size="small"
      className={[
        "floatingChat",
        isMinimized ? "floatingChat--minimized" : "",
        isFlashing ? "floatingChat--flash" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={{
        width: isMinimized ? 240 : 340,
        height: isMinimized ? DEFAULT_CHAT_MINIMIZED_HEIGHT : DEFAULT_CHAT_HEIGHT,
        ...style,
      }}
      title={headerNode}
      extra={headerActions}
      styles={{
        header: {
          background: primaryColor,
          borderBottom: "none",
          padding: "8px 10px",
        },
        body: {
          display: isMinimized ? "none" : "flex",
          flexDirection: "column",
          height: isMinimized ? 0 : DEFAULT_CHAT_HEIGHT - 52,
          minHeight: 0,
          padding: 10,
          background: "white",
        },
      }}
    >
      {!isMinimized && (
        <div className="floatingChat__body">
          <div ref={scrollContainerRef} className="floatingChat__messages" onScroll={handleScroll}>
            {hasMore && (
              <div className="floatingChat__loadMoreRow">
                <Button size="small" onClick={requestLoadMore}>
                  {t("chat.loadMore", { defaultValue: "Load older messages" })}
                </Button>
              </div>
            )}
            {messages.length === 0 ? (
              <Typography.Text type="secondary">{t("chat.noMessage")}</Typography.Text>
            ) : (
              Object.entries(groupedMessages).map(([dateKey, msgs]) => {
                const formattedDate = msgs[0]._dateObj.toLocaleDateString(localeTag, {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                });

                return (
                  <div key={dateKey} className="floatingChat__dayGroup">
                    <div className="floatingChat__dayLabelRow">
                      <span className="floatingChat__dayLabel">{formattedDate}</span>
                    </div>

                    {msgs.map((msg, idx) => {
                      const isFleet = isFleetMessage(msg);
                      const messageTime = msg._dateObj.toLocaleTimeString(localeTag, {
                        hour: "2-digit",
                        minute: "2-digit",
                      });
                      return (
                        <div
                          key={msg.docId || msg.tempMessageId || `${dateKey}-${idx}`}
                          className={`floatingChat__messageRow ${
                            isFleet ? "floatingChat__messageRow--fleet" : "floatingChat__messageRow--driver"
                          }`}
                        >
                          <div
                            className="floatingChat__messageBubble"
                            style={{ background: isFleet ? fleetColor : driverColor }}
                          >
                            <Typography.Text className="floatingChat__messageText">
                              {msg._displayMessage}
                            </Typography.Text>
                            <div
                              className={`floatingChat__messageTimeRow ${
                                isFleet
                                  ? "floatingChat__messageTimeRow--fleet"
                                  : "floatingChat__messageTimeRow--driver"
                              }`}
                            >
                              <Typography.Text type="secondary" className="floatingChat__messageTime">
                                {messageTime}
                              </Typography.Text>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>

          <div className="floatingChat__composer">
            <Input
              ref={inputRef}
              size="small"
              placeholder={t("chat.writeMessage")}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <Button type="primary" size="small" icon={<ArrowUpOutlined />} onClick={handleSend} loading={isSending} />
          </div>
        </div>
      )}
    </Card>
  );
};

export default FloatingChat;
